import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calculator, CreditCard, FileSearch, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const UGL_LOGO = "/ugl-logo.png";

const TOOLS = [
  { id: "bonus", label: "Bonus Italia 2026", desc: "Simulatore bonus nazionali, comunali e AI", icon: Calculator, src: "/bonus.html" },
  { id: "iban", label: "Verifica IBAN", desc: "Controlla validità, struttura e banca dell'IBAN", icon: CreditCard, src: "/utility-iban.html" },
  { id: "ateco", label: "Trova il tuo ATECO", desc: "Test guidato per trovare il codice ATECO per la P.IVA", icon: FileSearch, src: "/utility-ateco.html" },
];

export default function UtilityPage() {
  const [activeTool, setActiveTool] = useState(null);

  return (
    <div className="h-screen flex flex-col bg-[#0f1d32]">
      {/* Header */}
      <header className="bg-[#001830]/90 border-b border-white/10 flex-shrink-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-12">
          <Link to="/" className="flex items-center gap-2">
            <img src={UGL_LOGO} alt="UGL" className="w-7 h-7 object-contain" />
            <span className="font-semibold text-white text-sm hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>UGL Lariano</span>
          </Link>
          <div className="flex items-center gap-3">
            {activeTool && (
              <button onClick={() => setActiveTool(null)} className="flex items-center gap-1 text-xs text-[#C9A84C] hover:text-[#e0c060] transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Tutti gli strumenti
              </button>
            )}
            <span className="text-xs text-white/40 hidden sm:block">{activeTool ? TOOLS.find(t => t.id === activeTool)?.label : 'Strumenti'}</span>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/5 h-8 px-3 text-xs">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      {activeTool ? (
        <iframe
          src={TOOLS.find(t => t.id === activeTool)?.src}
          title={TOOLS.find(t => t.id === activeTool)?.label}
          className="flex-1 w-full border-0"
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
            <div className="text-center mb-8">
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#C9A84C] mb-2">Area</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Strumenti
              </h1>
              <p className="text-sm text-white/40 mt-1">Strumenti utili per i lavoratori e gli iscritti</p>
              <div className="flex items-center justify-center gap-3 mt-3">
                <div className="h-px w-12 bg-[#C9A84C]/40" />
                <div className="w-2 h-2 rotate-45 border border-[#C9A84C]/40" />
                <div className="h-px w-12 bg-[#C9A84C]/40" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TOOLS.map(tool => (
                <button key={tool.id} onClick={() => setActiveTool(tool.id)}
                  className="bg-[#13243d] border border-[#1e3456] rounded-2xl p-6 text-left hover:border-[#C9A84C]/30 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-1 transition-all group"
                  data-testid={`tool-${tool.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center mb-4 group-hover:bg-[#C9A84C]/20 transition-colors">
                      <tool.icon className="w-6 h-6 text-[#C9A84C]" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/15 group-hover:text-[#C9A84C] transition-colors" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1 group-hover:text-[#C9A84C] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>{tool.label}</h3>
                  <p className="text-xs text-white/40">{tool.desc}</p>
                </button>
              ))}

              {/* Placeholder per futuri tool */}
              <div className="border-2 border-dashed border-[#1e3456] rounded-2xl p-6 flex flex-col items-center justify-center text-center opacity-40">
                <Calculator className="w-8 h-8 text-white/20 mb-2" />
                <p className="text-xs text-white/30">Prossimamente altri strumenti</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
